import config from '../../config';
import API from '../../services/api';

const normalizeKey = key => String(key).replace(/[^a-z0-9]/gi, '').toLowerCase();

export const mobileMetricDimensions = {
  impressions: 'digitImpressions',
  reach: 'digitReach',
  frequency: 'digitFrequency'
};

const isMetricValue = value =>
  (typeof value === 'number' && Number.isFinite(value)) ||
  (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value.replace(/,/g, ''))));

const findMetricValue = (source, aliases, allowAnyNumericValue = false) => {
  if (isMetricValue(source)) return source;
  if (!source || typeof source !== 'object') return undefined;

  const normalizedAliases = new Set(aliases.map(normalizeKey));
  const entries = Array.isArray(source) ? source.map((value, index) => [String(index), value]) : Object.entries(source);

  for (const [key, value] of entries) {
    if (!normalizedAliases.has(normalizeKey(key))) continue;
    const metricValue = findMetricValue(value, aliases, true);
    if (metricValue !== undefined) return metricValue;
  }

  for (const [, value] of entries) {
    if (!value || typeof value !== 'object') continue;
    const metricValue = findMetricValue(value, aliases, allowAnyNumericValue);
    if (metricValue !== undefined) return metricValue;
  }

  if (allowAnyNumericValue) {
    const numericEntry = entries.find(([, value]) => isMetricValue(value));
    if (numericEntry) return numericEntry[1];
  }

  return undefined;
};

export const extractMobileMetric = (response, dimension) => {
  const metricName = Object.keys(mobileMetricDimensions).find(key => mobileMetricDimensions[key] === dimension);
  const aliases = [dimension, metricName, 'value', 'count', 'total'].filter(Boolean);
  const datasets = Array.isArray(response?.datasets) ? response.datasets : [];
  const matchingDataset = datasets.find(dataset =>
    [dataset?.label, dataset?.dimension, dataset?.name].some(key => normalizeKey(key) === normalizeKey(dimension))
  );
  const dataset = matchingDataset || datasets[0];

  if (isMetricValue(dataset?.value)) return dataset.value;

  const metricValue = findMetricValue(dataset?.value ?? dataset ?? response, aliases, true);
  return metricValue === undefined ? null : metricValue;
};

export const loadMobileSiteMetrics = async ({
  getMobileData,
  projectId,
  siteId,
  startTime,
  endTime,
  timezone,
  onError
}) => {
  const results = await Promise.all(
    Object.entries(mobileMetricDimensions).map(async ([metric, dimension]) => {
      const queryObject = {
        startTime,
        endTime,
        dimension,
        sites: [siteId],
        timezone
      };

      try {
        const response = await getMobileData(projectId, queryObject);
        return [metric, extractMobileMetric(response, dimension)];
      } catch (error) {
        if (typeof onError === 'function') onError(error, metric);
        return [metric, null];
      }
    })
  );

  return Object.fromEntries(results);
};

export const normalizeMobileSite = (site, index) => {
  const value = site?.value && typeof site.value === 'object' ? site.value : {};
  const siteId = value.siteId ?? value.site_id ?? '';

  return {
    label: site?.label ?? '--',
    displayName: value.siteName || value.site_name || site?.label || '--',
    latitude: value.latitude ?? '--',
    longitude: value.longitude ?? '--',
    id: `site-${index}-${siteId}`,
    siteId,
    storeId: value.store_id ?? value.storeId ?? '',
    maids: value.num_maids ?? value.numMaids ?? value.maids ?? 0,
    impressions: null,
    reach: null,
    frequency: null,
    signalVolumeDailyAvg: null,
    matchRate: null,
    metricsLoading: false,
    datasources: ['MOBILE_M']
  };
};

const getAnalyticsViewEndpoint = viewId => `${config.queryApi.baseUrl}/analytics/views/${viewId}:getData`;

const getAnalyticsValue = (response, resultKey) => {
  if (resultKey === 'scalar') return response?.scalar?.value ?? null;
  return response?.categorySeries?.points || [];
};

const requestAnalyticsView = async (viewId, body) => {
  await API.refreshTokenWhenNeeded();
  const response = await fetch(getAnalyticsViewEndpoint(viewId), {
    method: 'POST',
    headers: { ...API.headers, 'x-adm-request-id': crypto.randomUUID() },
    body: JSON.stringify({ viewId, viewVersion: 'v1', ...body })
  });

  return API.generateResponse(response);
};

export const loadAnalyticsSiteMetrics = async ({
  projectId,
  siteIds,
  segmentIds = [],
  startTime,
  endTime,
  timezone,
  onError
}) => {
  if (!projectId || !siteIds?.length) return {};

  const period = { startTime, endTime, timeZone: timezone || 'UTC' };
  const siteSelection = { siteIds };
  const analyticsBase = { scope: { projectId }, period, selection: siteSelection };

  const signalVolumeRequest = requestAnalyticsView('grouped_signal_volume_daily_avg', analyticsBase)
    .then(response => {
      const points = getAnalyticsValue(response, 'categorySeries');
      return Object.fromEntries(points.map(point => [point.key, point.value]));
    })
    .catch(error => {
      if (typeof onError === 'function') onError(error, 'signalVolumeDailyAvg');
      return {};
    });

  const matchRateRequest = segmentIds.length
    ? requestAnalyticsView('site_match_rate', {
        scope: { projectId },
        period,
        filters: { segmentIds }
      })
        .then(response => {
          const points = getAnalyticsValue(response, 'categorySeries');
          return points.reduce((values, point) => {
            values[point.key] = point.value;
            return values;
          }, {});
        })
        .catch(error => {
          if (typeof onError === 'function') onError(error, 'matchRate');
          return {};
        })
    : Promise.resolve({});

  const [signalVolumeBySite, matchRateBySite] = await Promise.all([signalVolumeRequest, matchRateRequest]);

  return siteIds.reduce(
    (metrics, siteId) => ({
      ...metrics,
      [siteId]: {
        signalVolumeDailyAvg: signalVolumeBySite[siteId] ?? null,
        matchRate: matchRateBySite[siteId] ?? null
      }
    }),
    {}
  );
};
